import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, ArrowUp, ArrowDown, FileText } from "lucide-react";

const EMPTY_FIELD = { key: "", label_pt: "", label_en: "", label_br: "", type: "text", required: false };
const FORM_KEY = "enrollment";

const TYPE_LABELS = { text: "Texto", textarea: "Texto longo", email: "Email", number: "Número" };

const INP = "w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm";

function LangRow({ base, label, textarea, cfg, set }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {["pt", "en", "br"].map((lg) => (
        <div key={lg}>
          <label className="block text-xs font-semibold text-[#0B1D3A]/60 mb-1 uppercase">{label} · {lg}</label>
          {textarea ? (
            <textarea rows="2" value={cfg[`${base}_${lg}`] || ""} onChange={(e) => set(`${base}_${lg}`, e.target.value)} className={INP} data-testid={`form-${base}-${lg}`} />
          ) : (
            <input value={cfg[`${base}_${lg}`] || ""} onChange={(e) => set(`${base}_${lg}`, e.target.value)} className={INP} data-testid={`form-${base}-${lg}`} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminForms() {
  const [cfg, setCfg] = useState(null);
  const [newField, setNewField] = useState(EMPTY_FIELD);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/forms/${FORM_KEY}`);
    setCfg({ ...data, fields: data.fields || [] });
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async (override) => {
    const payload = override || cfg;
    setSaving(true);
    try {
      const body = {
        title_pt: payload.title_pt || "", title_en: payload.title_en || "", title_br: payload.title_br || "",
        subtitle_pt: payload.subtitle_pt || "", subtitle_en: payload.subtitle_en || "", subtitle_br: payload.subtitle_br || "",
        success_pt: payload.success_pt || "", success_en: payload.success_en || "", success_br: payload.success_br || "",
        fields: payload.fields || [],
      };
      const { data } = await api.put(`/admin/forms/${FORM_KEY}`, body);
      setCfg({ ...data, fields: data.fields || [] });
      toast.success("Formulário guardado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  const addField = () => {
    if (!newField.key.trim() || !newField.label_pt.trim()) { toast.error("Preenche a chave e a etiqueta PT"); return; }
    const key = newField.key.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if ((cfg.fields || []).some((f) => f.key === key)) { toast.error("Já existe um campo com essa chave"); return; }
    const next = { ...cfg, fields: [...(cfg.fields || []), { ...newField, key }] };
    setNewField(EMPTY_FIELD);
    save(next);
  };

  const removeField = (key) => save({ ...cfg, fields: cfg.fields.filter((f) => f.key !== key) });

  const move = (i, dir) => {
    const arr = [...cfg.fields];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    save({ ...cfg, fields: arr });
  };

  if (!cfg) return <div className="text-[#0B1D3A]/60">A carregar...</div>;

  const inp = INP;

  return (
    <div data-testid="admin-forms-page">
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A] flex items-center gap-2"><FileText size={26} /> Formulários</h1>
        <button onClick={() => save()} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#1B5E3A] text-white font-semibold disabled:opacity-50" data-testid="form-save-btn"><Save size={16} /> {saving ? "A guardar..." : "Guardar"}</button>
      </div>
      <p className="text-sm text-[#0B1D3A]/55 mb-6">Formulário de <strong>Inscrição de Turmas</strong>. Edita os textos em PT / EN / BR e gere os campos adicionais. Os campos fixos (Concurso, Nome do professor, Email, Escola, País, Turma, Nº de alunos) estão sempre presentes.</p>

      <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 mb-6 space-y-5">
        <LangRow base="title" label="Título" cfg={cfg} set={set} />
        <LangRow base="subtitle" label="Subtítulo" textarea cfg={cfg} set={set} />
        <LangRow base="success" label="Mensagem de sucesso" textarea cfg={cfg} set={set} />
      </div>

      <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5" data-testid="form-fields-panel">
        <h2 className="font-outfit font-bold text-[#0B1D3A] mb-4">Campos adicionais</h2>
        <div className="space-y-2 mb-4">
          {(cfg.fields || []).map((f, i) => (
            <div key={f.key} className="flex items-center gap-3 text-sm bg-[#F8FAFC] rounded-lg px-3 py-2">
              <span className="font-mono text-[#005F73]">{f.key}</span>
              <span className="flex-1">{f.label_pt} / {f.label_en || "—"} / {f.label_br || "—"} <span className="text-[#0B1D3A]/40">({TYPE_LABELS[f.type] || f.type}{f.required ? ", obrigatório" : ""})</span></span>
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-[#0B1D3A]/50 disabled:opacity-30" data-testid={`form-field-up-${f.key}`}><ArrowUp size={15} /></button>
              <button onClick={() => move(i, 1)} disabled={i === cfg.fields.length - 1} className="text-[#0B1D3A]/50 disabled:opacity-30" data-testid={`form-field-down-${f.key}`}><ArrowDown size={15} /></button>
              <button onClick={() => removeField(f.key)} className="text-red-600" data-testid={`form-field-remove-${f.key}`}><Trash2 size={15} /></button>
            </div>
          ))}
          {(cfg.fields || []).length === 0 && <p className="text-sm text-[#0B1D3A]/50">Sem campos adicionais.</p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2 items-end border-t border-[#0B1D3A]/5 pt-4">
          <input placeholder="chave (ex: morada)" value={newField.key} onChange={(e) => setNewField({ ...newField, key: e.target.value })} className={inp} data-testid="form-newfield-key" />
          <input placeholder="Etiqueta PT" value={newField.label_pt} onChange={(e) => setNewField({ ...newField, label_pt: e.target.value })} className={inp} data-testid="form-newfield-pt" />
          <input placeholder="Label EN" value={newField.label_en} onChange={(e) => setNewField({ ...newField, label_en: e.target.value })} className={inp} data-testid="form-newfield-en" />
          <input placeholder="Etiqueta BR" value={newField.label_br} onChange={(e) => setNewField({ ...newField, label_br: e.target.value })} className={inp} data-testid="form-newfield-br" />
          <select value={newField.type} onChange={(e) => setNewField({ ...newField, type: e.target.value })} className={inp} data-testid="form-newfield-type">
            <option value="text">Texto</option>
            <option value="textarea">Texto longo</option>
            <option value="email">Email</option>
            <option value="number">Número</option>
          </select>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newField.required} onChange={(e) => setNewField({ ...newField, required: e.target.checked })} /> Obrigatório</label>
          <button onClick={addField} className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-[#0B1D3A] text-white font-semibold text-sm" data-testid="form-newfield-add"><Plus size={14} /> Adicionar</button>
        </div>
      </div>
    </div>
  );
}
